/**
 * AutoCycler — the auto-DJ that gives the visualizer its Geiss-style "always
 * dancing" feel.
 *
 * It owns no GPU/audio resources; it just makes scheduling decisions. Each
 * frame the engine asks `step(now, audio, beatTick)` and gets back either
 * `null` (stay on current child) or a child preset id to switch to.
 *
 * Trigger model:
 *   - Hard floor: never switch sooner than `minHoldMs` since the last switch.
 *     Prevents flicker if the user is hammering arrow keys or beats are noisy.
 *   - Hard ceiling: always switch by `maxHoldMs`. Prevents boredom if no beat
 *     ever lands or audio is muted.
 *   - Soft target: a randomized hold duration in [minHoldMs, maxHoldMs] that
 *     is shrunk by recent audio energy (rms1s) — louder/more intense passages
 *     cycle faster.
 *   - Beat alignment: once we're past the soft target, prefer to switch on
 *     the next beat tick. If no beat arrives within `beatBudgetMs`, switch
 *     anyway so silent input still cycles.
 *
 * Pick model:
 *   - Excludes the current child so we always actually change.
 *   - Otherwise uniform random over the candidate list.
 *
 * Determinism:
 *   - Construct with a custom RNG (`mulberry32`-style) for reproducible tests.
 *   - The default RNG is `Math.random`.
 */

export type AutoCyclerConfig = {
  /** Candidate child preset ids. Must contain at least 2 distinct ids. */
  candidates: readonly string[];
  /** Hard lower bound on time between switches. */
  minHoldMs: number;
  /** Hard upper bound on time between switches. */
  maxHoldMs: number;
  /**
   * How aggressively rms1s shrinks the soft target. 0 = ignore energy,
   * 1 = at peak energy the target compresses all the way to minHoldMs.
   */
  energyAcceleration: number;
  /** Try to align switches to a beat tick once past the soft target. */
  preferBeatTransition: boolean;
  /** Max time to wait for a beat past the soft target before switching anyway. */
  beatBudgetMs: number;
};

export const DEFAULT_AUTO_CYCLER_CONFIG: AutoCyclerConfig = {
  candidates: [],
  minHoldMs: 6000,
  maxHoldMs: 28000,
  energyAcceleration: 0.5,
  preferBeatTransition: true,
  beatBudgetMs: 2500,
};

export type AutoCyclerStatus = {
  enabled: boolean;
  /** Currently rendering child (== registry currentId at the moment we picked). */
  currentChildId: string | null;
  /**
   * Soft target ms remaining until the next preferred switch. Negative when
   * we're past the target and waiting on a beat. `null` when disabled.
   */
  msUntilNextTarget: number | null;
  /** True once past the soft target and within the beat-alignment window. */
  waitingForBeat: boolean;
  /** True if the upcoming switch will be forced by the hard ceiling. */
  forceImminent: boolean;
};

export type AutoCyclerStepInput = {
  rms1s: number;
  beatTick: boolean;
};

export type Rng = () => number;

/**
 * Mulberry32 — deterministic, fast, good enough for visual scheduling tests.
 */
export function mulberry32(seed: number): Rng {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export class AutoCycler {
  private enabled = false;
  private currentChildId: string | null = null;
  private lastSwitchAtMs = 0;
  /** Soft target ms (absolute, in `now` clock) we'd ideally switch at. */
  private softTargetAtMs = 0;
  private readonly rng: Rng;

  constructor(
    private config: AutoCyclerConfig,
    rng: Rng = Math.random,
  ) {
    this.rng = rng;
    if (config.candidates.length < 2) {
      throw new Error(
        `AutoCycler requires at least 2 candidates, got ${config.candidates.length}`,
      );
    }
    if (config.minHoldMs <= 0 || config.maxHoldMs <= config.minHoldMs) {
      throw new Error(
        `AutoCycler timing invariant violated: ` +
          `minHoldMs=${config.minHoldMs}, maxHoldMs=${config.maxHoldMs}`,
      );
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Enable the cycler and choose a starting child. Returns the picked id so
   * the engine can immediately load it without an awkward intermediate frame.
   */
  start(nowMs: number, exclude: string | null = null): string {
    this.enabled = true;
    this.currentChildId = exclude;
    const next = this.pickNext();
    this.currentChildId = next;
    this.lastSwitchAtMs = nowMs;
    this.scheduleSoftTarget(nowMs);
    return next;
  }

  /** Disable without changing the current child. The engine controls rendering. */
  stop(): void {
    this.enabled = false;
    this.currentChildId = null;
  }

  /**
   * Force an immediate switch to a new child (excluding the current one).
   * Resets the timing window. Used when the user hits arrow keys / space
   * while in auto mode — they want a new effect *now*.
   */
  forceSwitch(nowMs: number): string {
    const next = this.pickNext();
    this.currentChildId = next;
    this.lastSwitchAtMs = nowMs;
    this.scheduleSoftTarget(nowMs);
    return next;
  }

  /**
   * Per-frame tick. Returns the new child id if we decided to switch this
   * frame, otherwise `null`. The status block is always populated when the
   * cycler is enabled so the HUD can render countdowns.
   */
  step(
    nowMs: number,
    input: AutoCyclerStepInput,
  ): { switchTo: string | null; status: AutoCyclerStatus } {
    if (!this.enabled) {
      return {
        switchTo: null,
        status: {
          enabled: false,
          currentChildId: null,
          msUntilNextTarget: null,
          waitingForBeat: false,
          forceImminent: false,
        },
      };
    }

    const sinceSwitchMs = nowMs - this.lastSwitchAtMs;
    const energyShrinkMs = this.energyShrink(input.rms1s);
    const adjustedTargetAtMs = this.softTargetAtMs - energyShrinkMs;
    const msUntilTarget = adjustedTargetAtMs - nowMs;
    const pastTarget = msUntilTarget <= 0;
    const past = msUntilTarget < 0 ? -msUntilTarget : 0;

    const tooSoon = sinceSwitchMs < this.config.minHoldMs;
    const forceCeiling = sinceSwitchMs >= this.config.maxHoldMs;
    const beatGated = pastTarget && this.config.preferBeatTransition && input.beatTick;
    const beatTimeout =
      pastTarget && this.config.preferBeatTransition && past > this.config.beatBudgetMs;
    const targetReachedNoBeat = pastTarget && !this.config.preferBeatTransition;

    const shouldSwitch =
      !tooSoon && (forceCeiling || beatGated || beatTimeout || targetReachedNoBeat);

    let switchTo: string | null = null;
    if (shouldSwitch) {
      switchTo = this.pickNext();
      this.currentChildId = switchTo;
      this.lastSwitchAtMs = nowMs;
      this.scheduleSoftTarget(nowMs);
    }

    const refreshedTargetAtMs = shouldSwitch
      ? this.softTargetAtMs - this.energyShrink(input.rms1s)
      : adjustedTargetAtMs;
    const refreshedMsUntilTarget = refreshedTargetAtMs - nowMs;
    const status: AutoCyclerStatus = {
      enabled: true,
      currentChildId: this.currentChildId,
      msUntilNextTarget: refreshedMsUntilTarget,
      waitingForBeat: !shouldSwitch && pastTarget && this.config.preferBeatTransition,
      forceImminent:
        !shouldSwitch && this.config.maxHoldMs - sinceSwitchMs <= this.config.beatBudgetMs,
    };

    return { switchTo, status };
  }

  /**
   * Replace the candidate list. Useful if the registered preset pack changes.
   * The current child is preserved if still present; otherwise the next call
   * to `pickNext()` picks fresh.
   */
  setCandidates(candidates: readonly string[]): void {
    if (candidates.length < 2) {
      throw new Error(`AutoCycler requires at least 2 candidates`);
    }
    this.config = { ...this.config, candidates };
    if (this.currentChildId && !candidates.includes(this.currentChildId)) {
      this.currentChildId = null;
    }
  }

  private pickNext(): string {
    const candidates = this.config.candidates;
    const others = candidates.filter((c) => c !== this.currentChildId);
    const pool = others.length > 0 ? others : candidates;
    const idx = Math.floor(this.rng() * pool.length);
    return pool[Math.min(idx, pool.length - 1)] ?? pool[0] ?? '';
  }

  /** Schedule a fresh soft target in [minHoldMs, maxHoldMs] from `nowMs`. */
  private scheduleSoftTarget(nowMs: number): void {
    const span = this.config.maxHoldMs - this.config.minHoldMs;
    const offset = this.config.minHoldMs + this.rng() * span;
    this.softTargetAtMs = nowMs + offset;
  }

  /**
   * Compute how many ms to subtract from the soft target based on current
   * energy. Capped to the soft span so we never push the target into the
   * past-the-min-hold region.
   */
  private energyShrink(rms1s: number): number {
    if (this.config.energyAcceleration <= 0) return 0;
    const span = this.config.maxHoldMs - this.config.minHoldMs;
    const clamped = Math.max(0, Math.min(1, rms1s));
    return clamped * this.config.energyAcceleration * span;
  }
}
